import { useCallback, useEffect, useRef, useState } from 'react';
import type { FinanceFDPayload, FinanceFDPanel, FinanceFDValue } from './types';
const tabs = ['executif', 'departements', 'comparatif', 'projection'] as const;
function isValue(v: FinanceFDValue) {
  return v && (v.value === null || (typeof v.value === 'number' && Number.isFinite(v.value))) && typeof v.formatted === 'string' && typeof v.statusLabel === 'string' && ['closed', 'provisional', 'projected', 'unknown'].includes(v.status);
}
export function isFinanceFDPayload(value: unknown): value is FinanceFDPayload {
  if (!value || typeof value !== 'object') return false;
  const v = value as FinanceFDPayload;
  if (typeof v.title !== 'string' || typeof v.unknownLabel !== 'string') return false;
  if (v.state === 'blocked') return !!v.blocked && typeof v.blocked.title === 'string' && typeof v.blocked.message === 'string';
  if (v.state !== 'ready' || !v.panels) return false;
  return tabs.every(tab => {
    const p: FinanceFDPanel = v.panels[tab];
    return p && typeof p.title === 'string' && (!p.metrics || (Array.isArray(p.metrics) && p.metrics.every(m => isValue(m) && typeof m.label === 'string'))) && (!p.series || (Array.isArray(p.series) && p.series.every(s => typeof s.label === 'string' && Array.isArray(s.values) && s.values.every(m => isValue(m) && typeof m.label === 'string'))));
  });
}
// No localStorage, query cache, module-global payload or stale-response fallback.
export function useFinanceFD(identity?: string) {
  const [state, setState] = useState<{ identity: string; payload: FinanceFDPayload } | null>(null);
  const [error, setError] = useState('');
  const generation = useRef(0);
  const request = useRef<AbortController>();
  const clear = useCallback(() => { generation.current++; request.current?.abort(); setState(null); }, []);
  const refresh = useCallback(async () => {
    clear(); setError('');
    if (!identity || window.document.hidden) return;
    const current = generation.current;
    const controller = new AbortController(); request.current = controller;
    try {
      const response = await fetch('/api/ce/finance-fd-2026', { credentials: 'include', cache: 'no-store', signal: controller.signal });
      if (!response.ok) throw new Error('Accès privé indisponible ou refusé. Aucune donnée conservée à l’écran.');
      const payload: unknown = await response.json();
      if (!isFinanceFDPayload(payload)) throw new Error('Réponse privée invalide.');
      if (current === generation.current && !window.document.hidden) setState({ identity, payload });
    } catch (failure) {
      if (current === generation.current && !controller.signal.aborted) { setState(null); setError(failure instanceof Error ? failure.message : 'Consultation indisponible.'); }
    }
  }, [identity, clear]);
  useEffect(() => {
    void refresh();
    const visible = () => { if (window.document.hidden) clear(); else void refresh(); };
    const focus = () => { void refresh(); };
    window.document.addEventListener('visibilitychange', visible);
    window.addEventListener('focus', focus); window.addEventListener('pageshow', focus);
    window.addEventListener('pagehide', clear);
    const timer = window.setInterval(focus, 60_000);
    return () => { clear(); window.clearInterval(timer); window.document.removeEventListener('visibilitychange', visible); window.removeEventListener('focus', focus); window.removeEventListener('pageshow', focus); window.removeEventListener('pagehide', clear); };
  }, [refresh, clear]);
  return { payload: state && state.identity === identity ? state.payload : null, error, refresh };
}
